import { scrollRecentUpdatesFeedToStart } from './homeTourScroll';

const FEED_SCROLLER_SELECTOR = '[data-tour="home-recent-updates-scroller"]';
const WIDGET_SELECTOR = '#home-recent-updates-widget';

const getFeedScroller = (widget: Element): HTMLElement | null => {
  const scroller = widget.querySelector(FEED_SCROLLER_SELECTOR);
  return scroller instanceof HTMLElement ? scroller : null;
};

const isRectFullyInView = (rect: DOMRect, view: DOMRect): boolean =>
  rect.width > 0 &&
  rect.height > 0 &&
  rect.top >= view.top - 2 &&
  rect.bottom <= view.bottom + 2 &&
  rect.left >= view.left - 2 &&
  rect.right <= view.right + 2;

const isRectPartiallyInView = (rect: DOMRect, view: DOMRect): boolean =>
  rect.width > 0 &&
  rect.height > 0 &&
  rect.bottom > view.top + 4 &&
  rect.top < view.bottom - 4 &&
  rect.right > view.left + 4 &&
  rect.left < view.right - 4;

/**
 * Picks a reaction/comment tour target inside Recent Updates.
 *
 * querySelector alone always returns the first card's CTA, which can sit above
 * the Virtuoso clip when the feed is scrolled (spotlight sticks to the top edge).
 * Prefer a CTA fully visible in the feed; otherwise reset to the start and use
 * the first card — same idea as bringing a space card into the carousel view.
 */
export const resolveHomeRecentUpdatesTourElement = (
  selector: string,
  root: ParentNode = document
): Element | null => {
  const widget = root.querySelector(WIDGET_SELECTOR);
  if (!widget) {
    return null;
  }

  const candidates = Array.from(widget.querySelectorAll(selector));
  if (candidates.length === 0) {
    return null;
  }

  const scroller = getFeedScroller(widget);
  const viewEl = scroller ?? (widget instanceof HTMLElement ? widget : null);
  if (!viewEl) {
    return candidates[0];
  }

  const viewRect = viewEl.getBoundingClientRect();

  const fullyVisible = candidates.find((el) =>
    isRectFullyInView(el.getBoundingClientRect(), viewRect)
  );
  if (fullyVisible) {
    return fullyVisible;
  }

  const partiallyVisible = candidates.find((el) =>
    isRectPartiallyInView(el.getBoundingClientRect(), viewRect)
  );
  if (partiallyVisible instanceof HTMLElement && scroller) {
    // Nudge the feed so the visible card's CTA isn't clipped at the edge.
    const rect = partiallyVisible.getBoundingClientRect();
    const deltaTop = rect.top - (viewRect.top + 16);
    const deltaBottom = rect.bottom - (viewRect.bottom - 16);
    if (deltaTop < 0) {
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollTop += deltaTop;
    } else if (deltaBottom > 0) {
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollTop += deltaBottom;
    }
    return partiallyVisible;
  }

  // First-card CTA is above the clip (or nothing in view) — jump to start.
  scrollRecentUpdatesFeedToStart();
  return widget.querySelector(selector) ?? candidates[0];
};
